import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../../utils/api';
import { formatInUserTime } from '../../utils/date';
import { BrowserMultiFormatReader } from '@zxing/library';

export default function ScanOrder() {
  const { getToken } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [scanMode, setScanMode] = useState('camera');
  const [manualPayload, setManualPayload] = useState('');
  const [cameraActive, setCameraActive] = useState(false);
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const foundRef = useRef(false);
  const bottomRef = useRef(null);

  // Auto-scroll to bottom on mount so action button is visible above bottom nav on small phones
  useEffect(() => {
    const scrollToBottom = () => {
      const maxScroll = Math.max(
        document.body?.scrollHeight ?? 0,
        document.documentElement?.scrollHeight ?? 0
      ) - window.innerHeight;
      if (maxScroll > 0) {
        window.scrollTo({ left: 0, top: maxScroll });
      }
    };
    const t1 = setTimeout(scrollToBottom, 400);
    const t2 = setTimeout(scrollToBottom, 1000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const stopCamera = useCallback(() => {
    if (readerRef.current) {
      try { readerRef.current.reset(); } catch (_) {}
      readerRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
  }, []);

  useEffect(() => {
    return () => stopCamera();
  }, [stopCamera]);

  const fetchOrderByPayload = useCallback(async (payload) => {
    if (foundRef.current) return;
    foundRef.current = true;
    stopCamera();
    setLoading(true);
    setError(null);
    try {
      let parsed;
      try {
        parsed = JSON.parse(payload);
      } catch {
        throw new Error('Invalid QR format');
      }
      if (parsed?.type === 'reward' && parsed.claimId && parsed.token) {
        const validateRes = await api('/api/owners/rewards/validate-qr', {
          getToken,
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ payload }),
        });
        const validateData = await validateRes.json();
        if (!validateRes.ok) throw new Error(validateData.error || 'Invalid reward');
        setOrder({ _type: 'reward', ...validateData, token: parsed.token });
      } else {
        const res = await api(
          `/api/orders/by-qr?payload=${encodeURIComponent(payload)}`,
          { getToken }
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
        setOrder(data);
      }
    } catch (err) {
      setError(err.message);
      foundRef.current = false;
    } finally {
      setLoading(false);
    }
  }, [getToken, stopCamera]);

  const startCamera = useCallback(async () => {
    setError(null);
    setOrder(null);
    foundRef.current = false;
    stopCamera();

    try {
      const reader = new BrowserMultiFormatReader();
      readerRef.current = reader;

      await reader.decodeFromVideoDevice(undefined, videoRef.current, (result) => {
        if (result && !foundRef.current) {
          const payload = result.getText();
          try {
            const parsed = JSON.parse(payload);
            const isOrder = parsed.orderId && parsed.token;
            const isReward = parsed.type === 'reward' && parsed.claimId && parsed.token;
            if (isOrder || isReward) fetchOrderByPayload(payload);
          } catch {
            // not a valid QR, keep scanning
          }
        }
      });

      setCameraActive(true);
    } catch (err) {
      setError(err.message || 'Camera access denied');
    }
  }, [stopCamera, fetchOrderByPayload]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    const img = new Image();
    img.onload = async () => {
      try {
        const reader = new BrowserMultiFormatReader();
        const result = await reader.decodeFromImageElement(img);
        const payload = result.getText();
        await fetchOrderByPayload(payload);
      } catch (err) {
        setError('Could not decode QR from image');
      }
    };
    img.src = URL.createObjectURL(file);
    e.target.value = '';
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    const payload = manualPayload.trim();
    if (!payload) return;
    foundRef.current = false;
    fetchOrderByPayload(payload);
  };

  const handleBack = useCallback(() => {
    setOrder(null);
    setError(null);
    foundRef.current = false;
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <Link to="/owner/dashboard" className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 mb-1 inline-block">
          ← Back to dashboard
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
          Scan order QR
        </h1>
      </div>

      {!order ? (
        <>
          <div className="flex gap-2 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl">
            <button
              type="button"
              onClick={() => { setScanMode('camera'); setError(null); setOrder(null); foundRef.current = false; }}
              className={`flex-1 py-2 rounded-lg font-medium ${scanMode === 'camera' ? 'bg-white dark:bg-slate-700 shadow' : ''}`}
            >
              Camera
            </button>
            <button
              type="button"
              onClick={() => { setScanMode('file'); setError(null); stopCamera(); }}
              className={`flex-1 py-2 rounded-lg font-medium ${scanMode === 'file' ? 'bg-white dark:bg-slate-700 shadow' : ''}`}
            >
              Upload image
            </button>
            <button
              type="button"
              onClick={() => { setScanMode('manual'); setError(null); stopCamera(); }}
              className={`flex-1 py-2 rounded-lg font-medium ${scanMode === 'manual' ? 'bg-white dark:bg-slate-700 shadow' : ''}`}
            >
              Manual
            </button>
          </div>

          {scanMode === 'camera' && (
            <div className="space-y-4">
              <div className="relative aspect-square max-w-sm mx-auto rounded-xl overflow-hidden bg-slate-900">
                <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />
                {cameraActive && (
                  <div className="absolute inset-0 border-4 border-amber-500/50 rounded-xl pointer-events-none" />
                )}
              </div>
              <button
                type="button"
                onClick={cameraActive ? stopCamera : startCamera}
                className={`w-full py-3 rounded-lg font-medium ${
                  cameraActive
                    ? 'bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300'
                    : 'bg-amber-500 hover:bg-amber-600 text-slate-900'
                }`}
              >
                {cameraActive ? 'Stop camera' : 'Start camera'}
              </button>
            </div>
          )}

          {scanMode === 'file' && (
            <label className="block w-full py-8 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-amber-500 text-center cursor-pointer">
              <input type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
              <span className="text-slate-600 dark:text-slate-400">Choose image with QR code</span>
            </label>
          )}

          {scanMode === 'manual' && (
            <form onSubmit={handleManualSubmit} className="space-y-4">
              <textarea
                value={manualPayload}
                onChange={(e) => setManualPayload(e.target.value)}
                placeholder='{"orderId":123,"token":"abc123"}'
                rows={4}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 font-mono text-sm"
              />
              <button
                type="submit"
                disabled={loading || !manualPayload.trim()}
                className="w-full py-3 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-medium"
              >
                {loading ? 'Loading…' : 'Look up order'}
              </button>
            </form>
          )}

          {error && (
            <div className="p-4 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {loading && scanMode !== 'manual' && (
            <p className="text-center text-slate-600 dark:text-slate-400">Loading order…</p>
          )}
        </>
      ) : order?._type === 'reward' ? (
        <RewardRedeemView reward={order} onBack={handleBack} getToken={getToken} onRedeemed={() => setOrder(null)} />
      ) : (
        <OrderPickupView order={order} onBack={handleBack} getToken={getToken} onUpdate={setOrder} />
      )}
      <div ref={bottomRef} aria-hidden="true" className="h-0" />
    </div>
  );
}

function RewardRedeemView({ reward, onBack, getToken, onRedeemed }) {
  const [loading, setLoading] = useState(false);
  const [redeemed, setRedeemed] = useState(false);

  const handleRedeem = async () => {
    setLoading(true);
    try {
      const res = await api('/api/owners/rewards/redeem', {
        getToken,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ claimId: reward.claimId, token: reward.token }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Redeem failed');
      setRedeemed(true);
      setTimeout(() => onRedeemed?.(), 2000);
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (redeemed) {
    return (
      <div className="space-y-4">
        <div className="p-6 rounded-xl bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-center text-lg font-medium">
          Reward redeemed. Thank you!
        </div>
        <button type="button" onClick={onBack} className="w-full py-3 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700">
          Scan another
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
        <p className="font-semibold text-slate-900 dark:text-slate-100 mb-2">Customer reward</p>
        <p className="text-slate-700 dark:text-slate-300">{reward.label}</p>
      </div>
      <button
        type="button"
        onClick={handleRedeem}
        disabled={loading}
        className="w-full py-4 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-medium text-lg min-h-[44px]"
      >
        {loading ? 'Redeeming…' : 'Mark gift customer reward'}
      </button>
      <button type="button" onClick={onBack} className="w-full py-3 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700">
        Cancel
      </button>
    </div>
  );
}

function OrderPickupView({ order, onBack, getToken, onUpdate }) {
  const [loading, setLoading] = useState(false);

  const markPickedUp = async (orderItemId) => {
    setLoading(true);
    try {
      const res = await api(
        `/api/owners/me/orders/${order.id}/item/${orderItemId}/picked-up`,
        { getToken, method: 'PUT' }
      );
      if (!res.ok) throw new Error((await res.json()).error);
      const updated = { ...order, items: order.items.map((i) => (i.id === orderItemId ? { ...i, pickedUpAt: new Date().toISOString() } : i)) };
      onUpdate(updated);
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const completePickup = async () => {
    setLoading(true);
    try {
      const res = await api(
        `/api/owners/me/orders/${order.id}/complete-pickup`,
        { getToken, method: 'PUT' }
      );
      if (!res.ok) throw new Error((await res.json()).error);
      onUpdate({ ...order, status: 'picked_up' });
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefund = async () => {
    if (!window.confirm('Issue full refund for this order? This cannot be undone.')) return;
    setLoading(true);
    try {
      const res = await api(`/api/owners/me/orders/${order.id}/refund`, { getToken, method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
      onUpdate({ ...order, status: 'refunded' });
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const allPickedUp = order.items?.every((i) => i.pickedUpAt);
  const canComplete = allPickedUp && order.status !== 'picked_up';

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
        <p className="font-semibold text-slate-900 dark:text-slate-100">Order #{order.id}</p>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          ${(order.totalCents / 100).toFixed(2)} · {formatInUserTime(order.createdAt)}
        </p>
      </div>

      <div className="space-y-2">
        {order.items?.map((item) => (
          <div
            key={item.id}
            className="flex items-center justify-between p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
          >
            <div>
              <p className="font-medium text-slate-900 dark:text-slate-100">
                {item.name} × {item.quantity}
              </p>
              {item.notes && (
                <p className="text-sm text-slate-500 dark:text-slate-400 italic">{item.notes}</p>
              )}
            </div>
            {item.pickedUpAt ? (
              <span className="text-green-600 dark:text-green-400 font-medium">✓ Picked up</span>
            ) : (
              <button
                type="button"
                onClick={() => markPickedUp(item.id)}
                disabled={loading}
                className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-medium min-h-[44px]"
              >
                Picked up
              </button>
            )}
          </div>
        ))}
      </div>

      {canComplete && (
        <button
          type="button"
          onClick={completePickup}
          disabled={loading}
          className="w-full py-4 rounded-xl bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white font-medium text-lg"
        >
          Complete pickup
        </button>
      )}

      {order.status === 'picked_up' && (
        <div className="p-4 rounded-xl bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-center">
          Order complete
        </div>
      )}

      {order.status !== 'refunded' && order.status !== 'pending' && (
        <button
          type="button"
          onClick={handleRefund}
          disabled={loading}
          className="w-full py-3 rounded-xl border border-red-500 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 font-medium"
        >
          Refund
        </button>
      )}

      {order.status === 'refunded' && (
        <div className="p-4 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-center">
          Refunded
        </div>
      )}

      <button
        type="button"
        onClick={onBack}
        className="w-full py-3 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700"
      >
        Scan another
      </button>
    </div>
  );
}
